import type {
  OrgModelEntity,
  OrgModelProjection,
  PositionComputedState,
  PositionOccupancyVisibility,
  PositionStaffingStatus,
} from '../models/OrgModelProjection';
import type { PositionChartGroupingPolicy } from '../models/PositionChartGrouping';

export interface PositionChartGroupOccupant {
  readonly assignmentId: string;
  readonly personId: string;
  readonly displayLabel: string;
  readonly assignmentType: PositionComputedState['occupants'][number]['assignmentType'];
  readonly positionId: string;
}

export interface PositionChartGroup {
  readonly id: string;
  readonly label: string;
  readonly department: string | null;
  readonly location: string | null;
  readonly level: string | null;
  readonly managerPositionId: string | null;
  readonly positionIds: readonly string[];
  readonly positions: readonly OrgModelEntity[];
  readonly occupancyVisibility: PositionOccupancyVisibility;
  readonly staffingStatus: PositionStaffingStatus;
  readonly occupants: readonly PositionChartGroupOccupant[];
  readonly approvedCount: number;
  readonly staffedCount: number | null;
  readonly vacantCount: number | null;
  readonly successorCount: number;
}

export interface PositionChartGroupEdge {
  readonly id: string;
  readonly from: string;
  readonly to: string;
}

export interface PositionChartGroupingProjection {
  readonly groups: readonly PositionChartGroup[];
  readonly edges: readonly PositionChartGroupEdge[];
  readonly groupByPositionId: ReadonlyMap<string, PositionChartGroup>;
  readonly groupById: ReadonlyMap<string, PositionChartGroup>;
}

export function resolveSelectedPositionChartGroup(
  projection: PositionChartGroupingProjection,
  selectedIds: ReadonlySet<string>,
  activePositionId?: string | null,
): PositionChartGroup | null {
  for (const id of selectedIds) {
    const group = projection.groupById.get(id) ?? projection.groupByPositionId.get(id);
    if (group) return group;
  }
  return activePositionId
    ? projection.groupByPositionId.get(activePositionId) ?? null
    : null;
}

/**
 * Builds display groups only. Each member remains an independently addressable
 * canonical Position record for staffing, planning and properties mutations.
 */
export function projectPositionChartGroups(
  model: OrgModelProjection,
  policy: PositionChartGroupingPolicy,
): PositionChartGroupingProjection {
  const stateByPositionId = new Map(
    model.positionState.map((state) => [state.positionId, state]),
  );
  const relationshipManagerByPositionId = new Map(
    model.relationships
      .filter((relationship) => relationship.predicate === 'reports_to')
      .map((relationship) => [relationship.subjectRef, relationship.objectRef]),
  );
  const buckets = new Map<string, OrgModelEntity[]>();
  for (const position of model.positions) {
    const managerPositionId = stateByPositionId.get(position.id)?.managerPositionId
      ?? relationshipManagerByPositionId.get(position.id)
      ?? null;
    const key = policy.mode === 'individual'
      ? `seat\u0000${position.id}`
      : groupKey(position, managerPositionId, policy);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(position);
    else buckets.set(key, [position]);
  }

  const groups = [...buckets.values()]
    .map((positions) => createGroup(positions, stateByPositionId, relationshipManagerByPositionId))
    .sort((left, right) => left.label.localeCompare(right.label) || left.id.localeCompare(right.id));
  const groupByPositionId = new Map<string, PositionChartGroup>();
  const groupById = new Map<string, PositionChartGroup>();
  for (const group of groups) {
    groupById.set(group.id, group);
    for (const positionId of group.positionIds) groupByPositionId.set(positionId, group);
  }

  const edgeKeys = new Set<string>();
  const edges: PositionChartGroupEdge[] = [];
  for (const group of groups) {
    if (!group.managerPositionId) continue;
    const managerGroup = groupByPositionId.get(group.managerPositionId);
    if (!managerGroup || managerGroup.id === group.id) continue;
    const edgeKey = `${group.id}\u0000${managerGroup.id}`;
    if (edgeKeys.has(edgeKey)) continue;
    edgeKeys.add(edgeKey);
    edges.push({
      id: `position-group-edge:${stableHash(edgeKey)}`,
      from: group.id,
      to: managerGroup.id,
    });
  }
  edges.sort((left, right) => left.id.localeCompare(right.id));

  return { groups, edges, groupByPositionId, groupById };
}

export function positionChartGroupTargetPositionId(
  group: PositionChartGroup | null | undefined,
  stateByPositionId: ReadonlyMap<string, PositionComputedState>,
  preferredPositionId?: string | null,
): string | null {
  if (!group) return null;
  const available = group.positionIds.filter((positionId) => {
    const state = stateByPositionId.get(positionId);
    return state?.occupancyVisibility === 'visible' && !state.primaryOccupantPersonId;
  });
  if (preferredPositionId && available.includes(preferredPositionId)) return preferredPositionId;
  return available[0] ?? null;
}

export function positionChartSeatLabel(position: OrgModelEntity): string {
  return position.externalId
    ?? readString(position.props.positionCode)
    ?? readString(position.props.positionId)
    ?? position.id;
}

function groupKey(
  position: OrgModelEntity,
  managerPositionId: string | null,
  policy: PositionChartGroupingPolicy,
): string {
  const role = policy.roleKey === 'job_code'
    ? firstString(position.props, ['jobCode', 'job_code', 'roleCode', 'roleProfileCode'])
      ?? `title:${normalize(position.label)}`
    : normalize(position.label);
  const department = policy.includeDepartment
    ? normalize(firstString(position.props, ['department', 'departmentName']) ?? '')
    : '';
  const location = policy.includeLocation
    ? normalize(firstString(position.props, ['location', 'workLocation', 'officeLocation']) ?? '')
    : '';
  return [managerPositionId ?? 'root', role, department, location].join('\u0000');
}

function createGroup(
  positions: OrgModelEntity[],
  stateByPositionId: ReadonlyMap<string, PositionComputedState>,
  relationshipManagerByPositionId: ReadonlyMap<string, string>,
): PositionChartGroup {
  const sorted = [...positions].sort((left, right) =>
    positionChartSeatLabel(left).localeCompare(positionChartSeatLabel(right))
    || left.id.localeCompare(right.id));
  const states = sorted
    .map((position) => stateByPositionId.get(position.id))
    .filter((state): state is PositionComputedState => Boolean(state));
  const visibility = aggregateVisibility(states, sorted.length);
  const occupants = visibility === 'visible'
    ? states.flatMap((state) => state.occupants.map((occupant) => ({
      assignmentId: occupant.assignmentId,
      personId: occupant.personId,
      displayLabel: occupant.displayLabel,
      assignmentType: occupant.assignmentType,
      positionId: state.positionId,
    })))
    : [];
  const staffedCount = visibility === 'visible'
    ? sorted.filter((position) => {
      const state = stateByPositionId.get(position.id);
      return Boolean(state?.primaryOccupantPersonId || state?.occupants.length);
    }).length
    : null;
  const approvedCount = sorted.length;
  const vacantCount = staffedCount === null ? null : approvedCount - staffedCount;
  const first = sorted[0]!;
  const managerPositionId = stateByPositionId.get(first.id)?.managerPositionId
    ?? relationshipManagerByPositionId.get(first.id)
    ?? null;
  const positionIds = sorted.map((position) => position.id);
  return {
    id: positionIds.length === 1 ? positionIds[0]! : `position-group:${stableHash(positionIds.join('\u0000'))}`,
    label: first.label,
    department: commonString(sorted, ['department', 'departmentName']),
    location: commonString(sorted, ['location', 'workLocation', 'officeLocation']),
    level: commonString(sorted, ['level', 'managementLevel']),
    managerPositionId,
    positionIds,
    positions: sorted,
    occupancyVisibility: visibility,
    staffingStatus: aggregateStaffingStatus(staffedCount, approvedCount),
    occupants,
    approvedCount,
    staffedCount,
    vacantCount,
    successorCount: states.reduce((total, state) => total + state.successorCount, 0),
  };
}

function aggregateVisibility(
  states: readonly PositionComputedState[],
  positionCount: number,
): PositionOccupancyVisibility {
  if (states.length === positionCount && states.every((state) => state.occupancyVisibility === 'visible')) {
    return 'visible';
  }
  if (states.length > 0 && states.every((state) => state.occupancyVisibility === 'redacted')) {
    return 'redacted';
  }
  return 'partial';
}

function aggregateStaffingStatus(
  staffedCount: number | null,
  approvedCount: number,
): PositionStaffingStatus {
  if (staffedCount === null) return 'unknown';
  if (staffedCount === 0) return 'vacant';
  if (staffedCount === approvedCount) return 'filled';
  return 'covered';
}

function commonString(positions: readonly OrgModelEntity[], keys: readonly string[]): string | null {
  const values = positions.map((position) => firstString(position.props, keys));
  const first = values[0] ?? null;
  return first && values.every((value) => value === first) ? first : null;
}

function firstString(source: Readonly<Record<string, unknown>>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = readString(source[key]);
    if (value) return value;
  }
  return null;
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function normalize(value: string): string {
  return value.trim().toLocaleLowerCase().replace(/\s+/g, ' ');
}

function stableHash(value: string): string {
  let first = 2166136261;
  let second = 2246822507;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    first = Math.imul(first ^ code, 16777619);
    second = Math.imul(second ^ code, 3266489917);
  }
  return `${(first >>> 0).toString(36)}${(second >>> 0).toString(36)}`;
}
